import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./tokenlyglobal.css";
import aboutPageImage from "./assets/aboutpage.png";

const DoctorRegistration = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    gender: "male",
    name: "",
    phone: "",
    pin: "",
    pinConfirm: "",
    city: "",
    regNum: "",
    terms: false
  });
  const [showPin, setShowPin] = useState({ p1: false, p2: false });
  const [status, setStatus] = useState({ loading: false, error: "", success: "" });
  const [errors, setErrors] = useState({});

  // Refs for form fields
  const nameRef = useRef(null);
  const pinRef = useRef(null);
  const pinConfirmRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Special validation for mobile and PIN fields
    if (name === 'phone') {
      // Only allow numbers, max 10 digits
      const cleanVal = value.replace(/[^0-9]/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: cleanVal }));
    } else if (name === 'pin' || name === 'pinConfirm') {
      // Only allow numbers, max 4 digits
      const cleanVal = value.replace(/[^0-9]/g, '').slice(0, 4);
      setFormData(prev => ({ ...prev, [name]: cleanVal }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    
    // Clear error
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleGenderSelect = (selectedGender) => {
    setFormData(prev => ({ ...prev, gender: selectedGender }));
  };

  const handlePinChange = (field, value, nextFieldRef) => {
    // Only allow numbers, max 4 digits
    const cleanVal = value.replace(/[^0-9]/g, '').slice(0, 4);
    setFormData(prev => ({ ...prev, [field]: cleanVal }));

    // Clear error
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }

    // Auto-advance
    if (cleanVal.length === 4 && nextFieldRef) {
      nextFieldRef.current?.focus();
    }
  };

  const togglePinVisibility = (field) => {
    setShowPin(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, error: "", success: "" });

    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Full Name is required";
    if (!formData.phone.trim()) newErrors.phone = "Mobile Number is required";
    else if (formData.phone.length !== 10) newErrors.phone = "Mobile Number must be exactly 10 digits";
    else if (!/^[0-9]{10}$/.test(formData.phone)) newErrors.phone = "Mobile Number must contain only numbers";
    if (!formData.regNum.trim()) newErrors.regNum = "Registration No. is required";
    if (!formData.city.trim()) newErrors.city = "City is required";
    if (formData.pin.length !== 4) newErrors.pin = "PIN must be exactly 4 digits";
    else if (!/^[0-9]{4}$/.test(formData.pin)) newErrors.pin = "PIN must contain only numbers";
    if (formData.pinConfirm.length !== 4) newErrors.pinConfirm = "Confirm PIN must be exactly 4 digits";
    else if (!/^[0-9]{4}$/.test(formData.pinConfirm)) newErrors.pinConfirm = "Confirm PIN must contain only numbers";
    else if (formData.pin !== formData.pinConfirm) newErrors.pinConfirm = "PINs don't match";
    if (!formData.terms) newErrors.terms = "Accept terms to continue";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setStatus({ loading: false, error: "Please fill in all required fields marked in red.", success: "" });
      
      // Focus on first error field
      const firstErrorField = Object.keys(newErrors)[0];
      if (firstErrorField === 'name' && nameRef.current) {
        nameRef.current.focus();
      } else if (firstErrorField === 'pin' && pinRef.current) {
        pinRef.current.focus();
      } else if (firstErrorField === 'pinConfirm' && pinConfirmRef.current) {
        pinConfirmRef.current.focus();
      } else {
        // For other fields, scroll to the first input with error
        const errorInput = document.querySelector(`input[name="${firstErrorField}"]`);
        if (errorInput) {
          errorInput.focus();
          errorInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      
      return;
    }

    setErrors({});

    try {
      const deviceInfo = {
        device_id: 'web_' + Date.now(),
        device_type: 'web'
      };

      const payload = {
        fullname: formData.name,
        gender: formData.gender,
        phone_code: "+91",
        phone: formData.phone,
        password: formData.pin,
        password_confirmation: formData.pinConfirm,
        city: formData.city,
        role_id: 3,
        reg_num: formData.regNum,
        ...deviceInfo
      };

      const res = await fetch("https://tokenly.biswaas.com/api/user/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const json = await res.json();

      if (!json.status) {
        throw new Error(json.message || "Registration failed. Please try again.");
      }

      setStatus({ loading: false, error: "", success: "Registration Successful! Redirecting..." });

      setTimeout(() => {
        navigate("/login");
      }, 2000);

    } catch (err) {
      setStatus({ loading: false, error: err.message || "An error occurred.", success: "" });
    }
  };

  return (
    <div className="min-vh-100" style={{ backgroundColor: "#f3f4f6", paddingTop: "0px" }}>
      {/* Custom CSS for validation */}
      <style>{`
        .border-danger-custom {
          border-color: #dc3545 !important;
          border-width: 2px !important;
          border-style: solid !important;
          box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
        }
        .form-check-input.border-danger-custom {
          border-color: #dc3545 !important;
          box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
        }
      `}</style>
      {/* Hero Section - Full Bleed */}
      <div className="position-relative mb-5" style={{ minHeight: "400px", marginTop: "0px" }}>
        <img
          src={aboutPageImage}
          alt="Doctor Professional"
          className="w-100 h-100 object-fit-cover"
          style={{ position: 'absolute', inset: 0, objectPosition: 'center', minHeight: "400px", top: "0px" }}
        />
        <div className="position-absolute w-100 h-100 start-0 top-0"
          style={{ background: 'linear-gradient(135deg, rgba(20, 83, 45, 0.8), rgba(0,0,0,0.7))' }}></div>

        <div className="position-relative text-white text-center py-5" style={{ zIndex: 1, paddingTop: "120px" }}>
          <div className="container py-5">
            <div className="row justify-content-center">
              <div className="col-lg-8">
                                <h1 className="display-3 fw-bold mb-4 animate-fade-in">Register as a Doctor</h1>
               <p className="lead mb-4 fs-5"> Onboard wtih us at zero cost</p>
                <p className="lead mb-4 fs-5">Join Tokenly's trusted network of healthcare professionals and expand your digital practice</p>
                <div className="d-flex justify-content-center gap-4 flex-wrap mb-4">
                  <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-shield-check text-warning fs-3"></i>
                    <span className="fw-semibold">Verified Platform</span>
                  </div>
                  {/* <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-people text-warning fs-3"></i>
                    <span className="fw-semibold">10,000+ Doctors</span>
                  </div> */}
                  <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-graph-up text-warning fs-3"></i>
                    <span className="fw-semibold">Grow Practice</span>
                  </div>
                </div>
                <div className="d-flex justify-content-center gap-3">
                  <button
                    onClick={() => {
                      const formSection = document.getElementById('registration-form');
                      formSection?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="btn btn-warning fw-bold px-3 py-2 rounded-3 shadow-sm"
                  >
                    Register as doctor
                  </button>
                  <button
                    onClick={() => navigate('/login')}
                    className="btn btn-outline-light fw-bold px-4 py-2 rounded-3"
                  >
                    Login
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Registration Section - Side by Side */}
      <div id="registration-form" className="container pb-5">
        <div className="row justify-content-center g-4">
          {/* Left Box - About/Info */}
          <div className="col-lg-6">
            <div className="bg-white rounded-4 shadow-sm h-100">
              <div className="p-4 p-md-5">
                <h2 className="fw-bold mb-3"> Why Choose Tokenly?</h2>
                <p className="text-muted mb-3 small">
                  Tokenly is India's fastest growing digital healthcare platform connecting verified doctors with patients nationwide.
                </p>

                <div className="mb-3">
                  <h5 className="fw-semibold mb-2">Benefits Include:</h5>
                  <div className="d-flex align-items-start gap-2 mb-2">
                    <i className="bi bi-lightning-charge text-success fs-5 flex-shrink-0"></i>
                    <div>
                      <h6 className="fw-semibold mb-0 small">Easy to Install & Start</h6>
                      <p className="small text-muted mb-0">Set up your digital token system in under 30 minutes</p>
                    </div>
                  </div>
                  <div className="d-flex align-items-start gap-2 mb-2">
                    <i className="bi bi-gift text-success fs-5 flex-shrink-0"></i>
                    <div>
                      <h6 className="fw-semibold mb-0 small">Completely Free Platform</h6>
                      <p className="small text-muted mb-0">No subscription fees, no renewal costs, and no hidden charges—ever</p>
                    </div>
                  </div>
                  <div className="d-flex align-items-start gap-2 mb-2">
                    <i className="bi bi-person-walking text-success fs-5 flex-shrink-0"></i>
                    <div>
                      <h6 className="fw-semibold mb-0 small">Zero Cost for Walk-In Patients</h6>
                      <p className="small text-muted mb-0">All tokens generated at the clinic reception remain 100% free</p>
                    </div>
                  </div>
                  <div className="d-flex align-items-start gap-2 mb-2">
                    <i className="bi bi-phone text-success fs-5 flex-shrink-0"></i>
                    <div>
                      <h6 className="fw-semibold mb-0 small">Simple Online Token Charges</h6>
                      <p className="small text-muted mb-0">Each patient gets 2 free online tokens only once when they first download and use the app; charges apply for online tokens after that</p>
                    </div>
                  </div>
                  <div className="d-flex align-items-start gap-2 mb-2">
                    <i className="bi bi-people text-success fs-5 flex-shrink-0"></i>
                    <div>
                      <h6 className="fw-semibold mb-0 small">Eliminates Long Queues</h6>
                      <p className="small text-muted mb-0">Patients can book their turn online, reducing crowding and improving flow</p>
                    </div>
                  </div>
                  <div className="d-flex align-items-start gap-2 mb-2">
                    <i className="bi bi-display text-success fs-5 flex-shrink-0"></i>
                    <div>
                      <h6 className="fw-semibold mb-0 small">Smart Digital Clinic Upgrade</h6>
                      <p className="small text-muted mb-0">Optional announcement display device and thermal printer for a fully digital setup</p>
                    </div>
                  </div>
                  <div className="d-flex align-items-start gap-2 mb-2">
                    <i className="bi bi-emoji-smile text-success fs-5 flex-shrink-0"></i>
                    <div>
                      <h6 className="fw-semibold mb-0 small">Better Patient Experience</h6>
                      <p className="small text-muted mb-0">Real-time announcements and organized turn-wise calling improve satisfaction</p>
                    </div>
                  </div>
                  <div className="d-flex align-items-start gap-2 mb-2">
                    <i className="bi bi-graph-up text-success fs-5 flex-shrink-0"></i>
                    <div>
                      <h6 className="fw-semibold mb-0 small">Higher Clinic Efficiency</h6>
                      <p className="small text-muted mb-0">Less reception workload and smoother patient management for the doctor</p>
                    </div>
                  </div>
                  <div className="d-flex align-items-start gap-2 mb-2">
                    <i className="bi bi-star text-success fs-5 flex-shrink-0"></i>
                    <div>
                      <h6 className="fw-semibold mb-0 small">Boosts Clinic Reputation</h6>
                      <p className="small text-muted mb-0">A modern, transparent, and professional system patients appreciate</p>
                    </div>
                  </div>
                  <div className="d-flex align-items-start gap-2 mb-2">
                    <i className="bi bi-telephone text-success fs-5 flex-shrink-0"></i>
                    <div>
                      <h6 className="fw-semibold mb-0 small">Dedicated Support</h6>
                      <p className="small text-muted mb-0">For onboarding or setup assistance, call 8130172666 anytime</p>
                    </div>
                  </div>
                </div>

                <div className="bg-light rounded-3 p-2 mt-3">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <i className="bi bi-heart-fill text-primary fs-5"></i>
                    <span className="fw-semibold small">10,000+ Patients Trust Us</span>
                  </div>
                  <p className="small text-muted mb-0">Join thousands of satisfied patients using our digital token system</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Box - Registration Form */}
          <div className="col-lg-6">
            <div className="bg-white rounded-4 shadow-lg h-100">
              <div className="bg-gradient-primary p-4 p-md-5 text-white text-center rounded-top-4" style={{ background: 'linear-gradient(135deg, #14532d, #0f4c2a)' }}>
                <h3 className="fw-bold mb-2">Complete Your Registration</h3>
                <p className="mb-0 opacity-90 small">Fill in your details to join our trusted network</p>
              </div>

              <div className="p-4 p-md-5">
                {status.error && (
                  <div className="alert alert-danger border-0 bg-danger text-white mb-4 rounded-3 d-flex align-items-center position-sticky top-0" style={{ zIndex: 10, animation: 'shake 0.5s' }}>
                    <i className="bi bi-exclamation-triangle-fill me-2"></i> 
                    <div>
                      <strong>Please complete the form:</strong> {status.error}
                    </div>
                  </div>
                )}
                {status.success && (
                  <div className="alert alert-success border-0 bg-success text-white mb-4 rounded-3 d-flex align-items-center">
                    <i className="bi bi-check-circle-fill me-2"></i> {status.success}
                  </div>
                )}

                <form onSubmit={handleSignup}>
                  {/* Gender Toggle */}
                  <div className="mb-4">
                    <label className="form-label small text-muted fw-bold ls-1">I AM</label>
                    <div className="d-flex gap-2">
                      {['male', 'female', 'other'].map((g) => (
                        <button
                          key={g}
                          type="button"
                          onClick={() => handleGenderSelect(g)}
                          className={`btn flex-fill py-2 text-capitalize fw-semibold rounded-3 border ${formData.gender === g ? 'btn-dark' : 'btn-outline-light text-dark border-secondary-subtle'}`}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Name Input */}
                  <div className="form-floating mb-3">
                    <input
                      ref={nameRef}
                      type="text"
                      className={`form-control bg-light border-0 ${errors.name ? 'border-danger-custom' : ''}`}
                      id="nameInput"
                      placeholder="Full Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      autoComplete="off"
                      style={errors.name ? { borderColor: '#dc3545', borderWidth: '2px' } : {}}
                    />
                    <label htmlFor="nameInput" className="text-secondary">Full Name (Dr.)</label>
                    {errors.name && (
                      <div className="text-danger small mt-1 fw-semibold d-flex align-items-center">
                        <i className="bi bi-exclamation-circle me-1"></i> {errors.name}
                      </div>
                    )}
                  </div>

                  {/* Phone & Reg Number Row */}
                  <div className="row g-3 mb-3">
                    <div className="col-md-6">
                      <div className="form-floating">
                        <input
                          type="tel"
                          className={`form-control bg-light border-0 ${errors.phone ? 'border-danger-custom' : ''}`}
                          id="phoneInput"
                          placeholder="Phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          autoComplete="off"
                          style={errors.phone ? { borderColor: '#dc3545', borderWidth: '2px' } : {}}
                        />
                        <label htmlFor="phoneInput" className="text-secondary">Mobile Number</label>
                        {errors.phone && (
                          <div className="text-danger small mt-1 fw-semibold d-flex align-items-center">
                            <i className="bi bi-exclamation-circle me-1"></i> {errors.phone}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating">
                        <input
                          type="text"
                          className={`form-control bg-light border-0 ${errors.regNum ? 'border-danger-custom' : ''}`}
                          id="regInput"
                          placeholder="Registration No"
                          name="regNum"
                          value={formData.regNum}
                          onChange={handleChange}
                          autoComplete="off"
                          style={errors.regNum ? { borderColor: '#dc3545', borderWidth: '2px' } : {}}
                        />
                        <label htmlFor="regInput" className="text-secondary">Medical Reg. Number</label>
                        {errors.regNum && (
                          <div className="text-danger small mt-1 fw-semibold d-flex align-items-center">
                            <i className="bi bi-exclamation-circle me-1"></i> {errors.regNum}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* City Input */}
                  <div className="form-floating mb-4">
                    <input
                      type="text"
                      className={`form-control bg-light border-0 ${errors.city ? 'border-danger-custom' : ''}`}
                      id="cityInput"
                      placeholder="City"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      autoComplete="off"
                      style={errors.city ? { borderColor: '#dc3545', borderWidth: '2px' } : {}}
                    />
                    <label htmlFor="cityInput" className="text-secondary">City of Practice</label>
                    {errors.city && (
                      <div className="text-danger small mt-1 fw-semibold d-flex align-items-center">
                        <i className="bi bi-exclamation-circle me-1"></i> {errors.city}
                      </div>
                    )}
                  </div>

                  <div className="mb-2">
                    <label className="form-label small text-muted fw-bold ls-1">SECURITY PIN</label>
                  </div>
                  <div className="row g-3 mb-4">
                    <div className="col-6">
                      <div className="input-group has-validation">
                        <input
                          ref={pinRef}
                          type={showPin.p1 ? "text" : "password"}
                          className={`form-control bg-light border-0 py-3 ${errors.pin ? 'border-danger-custom' : ''}`}
                          placeholder="Create PIN"
                          maxLength="4"
                          value={formData.pin}
                          onChange={(e) => handlePinChange('pin', e.target.value, pinConfirmRef)}
                          autoComplete="new-password"
                          style={errors.pin ? { borderColor: '#dc3545', borderWidth: '2px' } : {}}
                        />
                        <button
                          className="btn bg-light border-0 text-secondary"
                          type="button"
                          onClick={() => togglePinVisibility('p1')}
                        >
                          <i className={`bi ${showPin.p1 ? 'bi-eye' : 'bi-eye-slash'}`}></i>
                        </button>
                        {errors.pin && (
                          <div className="text-danger small mt-1 fw-semibold d-flex align-items-center">
                            <i className="bi bi-exclamation-circle me-1"></i> {errors.pin}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="input-group has-validation">
                        <input
                          ref={pinConfirmRef}
                          type={showPin.p2 ? "text" : "password"}
                          className={`form-control bg-light border-0 py-3 ${errors.pinConfirm ? 'border-danger-custom' : ''}`}
                          placeholder="Confirm PIN"
                          maxLength="4"
                          value={formData.pinConfirm}
                          onChange={(e) => handlePinChange('pinConfirm', e.target.value)}
                          autoComplete="new-password"
                          style={errors.pinConfirm ? { borderColor: '#dc3545', borderWidth: '2px' } : {}}
                        />
                        <button
                          className="btn bg-light border-0 text-secondary"
                          type="button"
                          onClick={() => togglePinVisibility('p2')}
                        >
                          <i className={`bi ${showPin.p2 ? 'bi-eye' : 'bi-eye-slash'}`}></i>
                        </button>
                        {errors.pinConfirm && (
                          <div className="text-danger small mt-1 fw-semibold d-flex align-items-center">
                            <i className="bi bi-exclamation-circle me-1"></i> {errors.pinConfirm}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="form-check mb-4">
                    <input
                      className={`form-check-input ${errors.terms ? 'border-danger-custom' : ''}`}
                      type="checkbox"
                      id="termsCheck"
                      name="terms"
                      checked={formData.terms}
                      onChange={handleChange}
                      style={errors.terms ? { borderColor: '#dc3545', borderWidth: '2px' } : {}}
                    />
                    <label className="form-check-label small text-secondary" htmlFor="termsCheck">
                      I agree to the <button type="button" className="btn btn-link p-0 fw-bold text-dark text-decoration-none">Terms of Service</button> & <button type="button" className="btn btn-link p-0 fw-bold text-dark text-decoration-none">Privacy Policy</button>
                    </label>
                    {errors.terms && (
                      <div className="text-danger small mt-2 fw-semibold d-flex align-items-center">
                        <i className="bi bi-exclamation-circle me-1"></i> {errors.terms}
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="btn btn-dark w-100 py-3 rounded-3 fw-bold shadow-sm d-flex align-items-center justify-content-center"
                    disabled={status.loading}
                  >
                    {status.loading ? (
                      <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                    ) : (
                      "Complete Registration"
                    )}
                  </button>

                  <div className="text-center mt-4">
                    <p className="text-muted small">
                      Already have an account?
                      <button type="button" onClick={() => navigate('/login')} className="btn btn-link p-0 ms-1 fw-bold text-dark text-decoration-none">Sign In</button>
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorRegistration;
